import React, { useEffect, useRef, useState } from "react";
import { Play } from "lucide-react";

// Muted teaser only (reel-style): the iframe plays a few seconds once the card
// scrolls into view, then unmounts back to a thumbnail — no hour-long stream
// silently running in the feed. Clicking (PostCard overlay) opens the theater.
const EMBED_PREVIEW_MS = 10000;

const embedThumb = (embed) => {
  if (embed.thumbnail) return embed.thumbnail;
  let vid = embed.video_id || embed.videoId;
  if (!vid && embed.provider === "youtube") {
    const m = String(embed.src || "").match(/\/embed\/([A-Za-z0-9_-]{6,})/);
    vid = m ? m[1] : null;
  }
  if (embed.provider === "youtube" && vid) return `https://i.ytimg.com/vi/${vid}/hqdefault.jpg`;
  return null;
};

// Connexion TCP+TLS préparée vers le fournisseur dès qu'une carte « vidéo par lien » entre à l'écran :
// la visionneuse (iframe neuve) gagne les 100-300 ms de poignée de main. Une fois par origine.
const EMBED_ORIGINS = {
  youtube: ["https://www.youtube.com", "https://i.ytimg.com", "https://www.google.com"],
  facebook: ["https://www.facebook.com", "https://static.xx.fbcdn.net"],
};
const _preconnected = new Set();
const preconnectEmbed = (provider) => {
  for (const origin of EMBED_ORIGINS[provider] || []) {
    if (_preconnected.has(origin)) continue;
    _preconnected.add(origin);
    const l = document.createElement("link");
    l.rel = "preconnect";
    l.href = origin;
    document.head.appendChild(l);
  }
};

// `sound` : lecture avec le son (visionneuse / théâtre) — la source des cartes du fil est un teaser muet.
const withSound = (src) => String(src || "").replace(/([?&])mute=1(&|$)/, "$1mute=0$2").replace(/([?&])mute=true(&|$)/, "$1mute=false$2");

export const EmbedPlayer = ({ embed, testId, preview = true, sound = false, className = "mt-3 rounded-2xl" }) => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  const [expired, setExpired] = useState(false);
  const [frameReady, setFrameReady] = useState(false);
  const [thumbBroken, setThumbBroken] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") { setInView(true); return; }
    const obs = new IntersectionObserver(([e]) => setInView(e.isIntersecting), { threshold: 0.35 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  useEffect(() => { if (inView) preconnectEmbed(embed?.provider); }, [inView, embed?.provider]);
  // Sécurité : si `onLoad` ne vient pas (lecteur bloqué), le lecteur est révélé quand même après 3 s.
  useEffect(() => {
    if (!inView || preview || frameReady) return undefined;
    const t = setTimeout(() => setFrameReady(true), 3000);
    return () => clearTimeout(t);
  }, [inView, preview, frameReady]);

  useEffect(() => {
    if (!preview) return undefined;
    if (!inView) { setExpired(false); return undefined; } // fresh teaser next time it scrolls in
    const t = setTimeout(() => setExpired(true), EMBED_PREVIEW_MS);
    return () => clearTimeout(t);
  }, [inView, preview]);

  const showIframe = inView && !(preview && expired);
  const thumb = embedThumb(embed);
  // Visionneuse / théâtre (preview=false) : la miniature reste affichée SOUS le lecteur jusqu'à ce que
  // l'iframe ait chargé (fondu) — plus d'écran noir pendant que YouTube/Facebook démarre.
  const holdThumb = !preview && thumb && !thumbBroken && !frameReady;
  return (
    <div ref={ref} className={`${className} overflow-hidden bg-black relative aspect-video`} data-testid={testId}>
      {showIframe ? (
        <>
          {holdThumb && (
            <img src={thumb} alt="" decoding="async" onError={() => setThumbBroken(true)} className="absolute inset-0 w-full h-full object-cover" data-testid={`${testId}-thumb-hold`} />
          )}
          <iframe
            src={sound ? withSound(embed.src) : embed.src}
            title={`${embed.provider} video`}
            className="relative w-full h-full border-0"
            style={{ opacity: holdThumb ? 0 : 1, transition: "opacity 220ms ease" }}
            onLoad={() => setFrameReady(true)}
            allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
            allowFullScreen
            data-testid={`${testId}-iframe`}
          />
        </>
      ) : (
        <>
          {thumb && (
            <img src={thumb} alt="" loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover" data-testid={`${testId}-thumb`} />
          )}
          <div className="absolute inset-0 grid place-items-center" style={{ background: "rgba(0,0,0,0.25)" }}>
            <span className="h-14 w-14 rounded-full grid place-items-center" style={{ background: "rgba(0,0,0,0.55)", backdropFilter: "blur(4px)" }}>
              <Play size={26} fill="white" className="text-white" />
            </span>
          </div>
        </>
      )}
    </div>
  );
};
